import { z } from "zod";
import { shortDay, timeLabel } from "./month-booking.ts";
import { uuidSchema } from "./schemas/common.ts";
import { localDate, localTime } from "./time.ts";

/**
 * Booking rules for one class (epic "Pilates studio shape", B3, migration 0073). A class can have
 * its own booking window ("clients can book up to 14 days ahead") and its own free-cancel time. A
 * studio opens private sessions 14 or 30 days ahead, and its group classes keep the business rules.
 *
 * The class writes its own `policies` row and `groups.policy_id` points at it, so the policy
 * engine, the booking check and the Book tab read the class rules with no second path. Null in
 * `ClassRules` means "the business default". The owner and admins set it on Desk; staff cannot.
 */

/** The fields a class sets for itself. Every other field of its row follows the business row. */
export const classRuleFields = ["max_book_ahead_days", "cancel_cutoff_hours"] as const;
export type ClassRuleField = (typeof classRuleFields)[number];

/** The windows the owner picks from, in days. */
export const CLASS_BOOK_AHEAD_DAYS = [7, 14, 30, 60] as const;
/** The free-cancel times the owner picks from, in hours before the session. */
export const CLASS_CANCEL_CUTOFF_HOURS = [2, 6, 12, 24, 48] as const;

const oneOf = (choices: readonly number[], what: string) =>
  z
    .number()
    .int()
    .refine((value) => choices.includes(value), { message: `Pick one of ${choices.join(", ")} ${what}.` });

/** The class rules. Null: the business default. */
export const classRulesSchema = z.object({
  maxBookAheadDays: oneOf(CLASS_BOOK_AHEAD_DAYS, "days").nullable(),
  cancelCutoffHours: oneOf(CLASS_CANCEL_CUTOFF_HOURS, "hours").nullable(),
});
export type ClassRules = z.infer<typeof classRulesSchema>;

/** `PUT /owner/businesses/:id/groups/:groupId/rules`. Both null: back to the business default. */
export const saveClassRulesRequestSchema = classRulesSchema;
export type SaveClassRulesRequest = z.infer<typeof saveClassRulesRequestSchema>;

/** `GET` and `PUT .../groups/:groupId/rules`: the class rules and the business values they replace. */
export const classRulesViewSchema = z.object({
  groupId: uuidSchema,
  rules: classRulesSchema,
  business: z.object({
    maxBookAheadDays: z.number().int(),
    cancelCutoffHours: z.number().int(),
  }),
});
export type ClassRulesView = z.infer<typeof classRulesViewSchema>;

/** The rules that apply to the class: its own, else the business values. */
export function effectiveClassRules(view: Pick<ClassRulesView, "rules" | "business">): { maxBookAheadDays: number; cancelCutoffHours: number } {
  return {
    maxBookAheadDays: view.rules.maxBookAheadDays ?? view.business.maxBookAheadDays,
    cancelCutoffHours: view.rules.cancelCutoffHours ?? view.business.cancelCutoffHours,
  };
}

/** True when the class has no rules of its own. */
export function usesBusinessDefault(rules: ClassRules): boolean {
  return rules.maxBookAheadDays === null && rules.cancelCutoffHours === null;
}

/** "1 day", "14 days". */
export function daysWords(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/** "1 hour", "48 hours". */
export function hoursWords(hours: number): string {
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/**
 * When a session opens for booking: "Opens on Mon 3 Nov at 7:00 AM", in the business timezone.
 * `opensAt` comes from the policy engine (`evaluateBooking`, reason `too_far_ahead`).
 */
export function opensOnWords(opensAt: Date, timezone: string): string {
  return `Opens on ${shortDay(localDate(opensAt, timezone))} at ${timeLabel(localTime(opensAt, timezone))}`;
}

/**
 * Why a session cannot be booked, from `evaluateBooking`, with the opening day when the window is
 * the reason: "Opens on Mon 3 Nov at 7:00 AM. You can book this session up to 14 days ahead. ..."
 * The server sends it as the refusal and as the reason on the Book tab.
 */
export function bookingWindowWords(outcome: { explanation: string; opensAt?: Date }, timezone: string): string {
  return outcome.opensAt ? `${opensOnWords(outcome.opensAt, timezone)}. ${outcome.explanation}` : outcome.explanation;
}
